import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'

//
import { Formik } from 'formik'
import moment from 'moment'
import * as Yup from 'yup'

import { InviteEmployeesFormValues } from 'core/application/adminDashboard/tasks/inviteEmployees'

import { Button, Col, DatePicker, DefaultCheckbox, Modal, Row, Tooltip } from 'common/components'
import { parseZoneFormat } from 'common/dateFormatters'
import { useCommandRequestV2 } from 'common/hooks'
import { ROUTE_PATHS } from 'common/routePaths'

//
import './styles.less'

type InvitePerksEmployeesFormProps = {
  visible: boolean
  inviteApi: (arg: InviteEmployeesFormValues) => Promise<any>
  benefitStartDate: string
  infoMessage: string
  title?: string
  checkboxLabel: string
  onSendInviteToEmployees?: () => void
  onCancel?: () => void
  showAllEmployeesAddedCheckBox?: boolean
}

const InviteYourEmployeesModal = ({
  visible,
  inviteApi,
  benefitStartDate,
  infoMessage,
  title,
  checkboxLabel,
  onSendInviteToEmployees,
  onCancel,
  showAllEmployeesAddedCheckBox,
}: InvitePerksEmployeesFormProps) => {
  const { t: translate } = useTranslation()
  const history = useHistory()

  const { sendCommand, isLoading: isCommandLoading } = useCommandRequestV2((req: InviteEmployeesFormValues) =>
    inviteApi(req),
  )

  const [isValidForm, setIsValidForm] = useState<boolean>()

  const formRef = useRef<HTMLFormElement | null>(null)

  const now = moment()
  const isNowGreaterThanBenefitStartDate = now.startOf('date').isAfter(moment(benefitStartDate))

  const onFormSubmit = () => {
    formRef.current?.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }))
  }

  return (
    <Modal
      className={'invite-perks-employees-modal-wrap'}
      onCancel={() => void (onCancel && onCancel())}
      visible={visible}
      title={title ?? translate('admin-dashboard.home.modal-title')}
      footer={
        <div>
          <Button onClick={() => void (onCancel && onCancel())}>{translate('general.cancel')}</Button>
          {!isValidForm ? (
            <Tooltip title={translate('admin-dashboard.home.invite-alert.select-date-label')} position="left">
              <InviteEmployeesButton
                loading={isCommandLoading}
                disable={isCommandLoading || !isValidForm}
                onClicked={onFormSubmit}
              />
            </Tooltip>
          ) : (
            <InviteEmployeesButton
              loading={isCommandLoading}
              disable={isCommandLoading || !isValidForm}
              onClicked={onFormSubmit}
            />
          )}
        </div>
      }
    >
      <Formik
        validationSchema={formValidator(translate)}
        initialValues={{
          notificationDate: null,
          allEmployeesAdded: !showAllEmployeesAddedCheckBox,
        }}
        onSubmit={async (values) => {
          const result = await sendCommand(values)
          result.completed && onSendInviteToEmployees?.()
        }}
        validate={(values) => setIsValidForm(formValidator(translate).isValidSync(values))}
      >
        {({ handleSubmit }) => (
          <form className="invite_employees_content" ref={formRef} autoComplete="off" onSubmit={handleSubmit}>
            {isNowGreaterThanBenefitStartDate && (
              <>
                <p>
                  Your benefit start date: <b>{parseZoneFormat(benefitStartDate)}</b> is in the past!
                </p>
                <p>
                  In order to notify your employees please go to your{' '}
                  <a
                    href={''}
                    onClick={() => {
                      history.push(ROUTE_PATHS.BENEFIT_DETAILS_PATH)
                    }}
                  >
                    benefit details
                  </a>{' '}
                  and change the benefit start date.
                </p>
              </>
            )}

            {!isNowGreaterThanBenefitStartDate && (
              <>
                <p className={'mb-24'}>{infoMessage}</p>

                <div>
                  <label className={'mb-8 d-block'}>
                    {translate('admin-dashboard.home.invite-alert.notification-label')}
                  </label>

                  <DatePicker
                    placeholder="Choose notification date"
                    name="notificationDate"
                    id="notificationDate"
                    noStyles
                    disabledDate={(d) =>
                      !d || d.isBefore(moment().subtract(1, 'day')) || d.isAfter(moment(benefitStartDate).add(1, 'day'))
                    }
                  />
                </div>

                {showAllEmployeesAddedCheckBox && (
                  <>
                    <Row>
                      <Col span={24}>
                        <DefaultCheckbox children={checkboxLabel} name="allEmployeesAdded" className="no-select" />
                      </Col>
                    </Row>

                    <Row className="mt-8">
                      <Col span={24}>
                        <div>
                          <p>
                            {translate('admin-dashboard.home.invite-alert.go-to')}
                            <a
                              href={`${ROUTE_PATHS.MANAGE_PEOPLE}?fromtask=true`}
                              rel="noreferrer"
                              className="link underline"
                            >
                              {translate('admin-dashboard.home.invite-alert.manage-people-link')}
                            </a>
                            {translate('admin-dashboard.home.invite-alert.last-part')}
                          </p>
                        </div>
                      </Col>
                    </Row>
                  </>
                )}
              </>
            )}
          </form>
        )}
      </Formik>
    </Modal>
  )
}

type InviteEmployeesButtonProps = {
  disable?: boolean
  loading?: boolean
  onClicked?: () => void
}
const InviteEmployeesButton = (props: InviteEmployeesButtonProps) => {
  return (
    <Button onClick={props.onClicked} type="primary" disabled={props.disable} loading={props.loading} className="ml-8">
      Send invite
    </Button>
  )
}

const formValidator = (translate: any) =>
  Yup.object({
    allEmployeesAdded: Yup.bool().oneOf([true], 'Required'),
    notificationDate: Yup.date().required(translate('general.required')).typeError(''),
  })

export default InviteYourEmployeesModal
